/**
 * Pure helpers for PageTypesCreateContainer (SCRUM-1032, following the SCRUM-910
 * pattern). No component state / dispatch / props here — inputs in, plain data
 * out — so each is unit-testable in isolation. The container keeps thin wrappers
 * that apply the returned values via setState / dispatch / the parent callback.
 *
 * getUnique / extractProductsFromSubcats were already module-scope pures in the
 * container (SCRUM-898); moved here so all the file's pure logic sits in one
 * tested place.
 */

/** A product wrapper (ProductProperties row) as a sub-category doc lists it. */
type ProductWrapper = {
  _id?: unknown;
  path?: string;
  product: { country_code?: string } | null;
};

/** A sub-category doc with its products. */
type SubcategoryDoc = { _id?: unknown; products: ProductWrapper[] };

/** An existing page, as far as the collision checks read it. */
type ExistingPage = {
  page_configuration?: {
    path?: string;
    type?: string;
    object_target?: { _id?: unknown } | string | null;
  };
};

/** A blog post, as far as the authors picker reads it. */
type BlogPostLike = {
  title?: string;
  pathauthor?: string;
  author?: { _id?: unknown; name?: string; avatar?: string } | null;
};

/** One entry of the authors picker. */
type AuthorEntry = {
  _id: unknown;
  name: string | undefined;
  avatar: string | undefined;
  pathauthor: string | undefined;
  posts: (string | undefined)[];
};

/**
 * Dedupe an array by a key field (`comp`), preserving first-seen order.
 */
export function getUnique<T>(arr: T[] | null | undefined, comp: string): T[] {
  if (!Array.isArray(arr)) return [];
  const seen = new Set<unknown>();
  const out: T[] = [];
  for (const it of arr) {
    const key = (it as Record<string, unknown> | null | undefined)?.[comp];
    if (key != null && !seen.has(key)) {
      seen.add(key);
      out.push(it);
    }
  }
  return out;
}

/**
 * From a list of sub-category docs, derive the deduped sub-categories and their
 * flattened, deduped, non-null product wrappers (country-filtered when the site
 * has a country_code).
 */
export function extractProductsFromSubcats(
  subcats: SubcategoryDoc[] | null | undefined,
  site: { country_code?: string } | null | undefined
) {
  const subcategories = getUnique(subcats || [], "_id");
  const subProd = subcategories.flatMap((sc) => sc.products);
  const products = getUnique(subProd || [], "_id").filter(
    (obj) =>
      obj.product !== null &&
      (!site?.country_code || obj.product.country_code === site.country_code)
  );
  return { subcategories, products };
}

/**
 * True when a product wrapper's derived path is NOT already taken by an existing
 * page — used as a filter to keep still-placeable products.
 *
 * NB (characterization-pinned, SCRUM-1032): `.replace(" ", "-")` replaces only
 * the FIRST space in the product path. Do NOT "fix" this to a global replace
 * here — the create-picker's collision check has always behaved this way and the
 * suite pins it.
 * `pages` are the site's pages (guarded below; a site without pages is a valid
 * caller); `prod` is the product wrapper.
 */
export function isProductPathFree(pages: ExistingPage[] | undefined, prod: { path?: string }): boolean {
  const testPath = prod.path?.replace(" ", "-") + "/";
  return !(pages || []).find((p) => p.page_configuration?.path === testPath);
}

/**
 * True when NO existing page already targets this sub-category — used as a filter
 * so the create-page picker offers only sub-categories that still need a listing
 * page. The sibling of isProductPathFree above, which does the same job for
 * products; the sub-category picker had no such filter (SCRUM-1452), so
 * bancompara.pt offered BANK_ACCOUNT_MAIN and BANK_ACCOUNT_BUSINESS although both
 * already carry a published listing (measured 2026-09-06).
 *
 * Matched on the target id, not on the path: a sub_category page's path is derived
 * from the site's own label for the sub-category (SCRUM-1105), so two sites reach
 * the same record by different paths, and the path is exactly the field that has
 * been found holding product names.
 *
 * `object_target` arrives in three shapes and all three are handled: an object
 * carrying `_id` — the full sub-category document (controllers/site.js) or the
 * projected target getPageById answers (`{_id, slug, category, products}`,
 * SCRUM-1651; a slim `{_id}` before it, SCRUM-907) — a bare id, and `null` when
 * the target no longer resolves — a null cannot identify a sub-category, so it
 * never excludes one.
 *
 * Auto-created tag pages clone their parent's page_configuration verbatim
 * (controllers/page.js), so they carry the same type and the same object_target.
 * A sub-category with tag pages is therefore matched by any of them, which is the
 * same answer its main page already gives. `pages` are guarded as above.
 */
export function isSubcategoryPageFree(
  pages: (ExistingPage | null)[] | undefined,
  subCategory: { _id?: unknown } | null | undefined
): boolean {
  const subId = subCategory?._id;
  if (subId == null) return true;
  return !(pages || []).some((p) => {
    const cfg = p?.page_configuration;
    if (cfg?.type !== "sub_category") return false;
    const target = cfg.object_target;
    const targetId =
      target && typeof target === "object" ? target._id : target;
    return targetId != null && String(targetId) === String(subId);
  });
}

/**
 * Derive the authors picker data from the blog doc — one entry per unique author
 * with the titles of that author's posts — plus the two availability flags.
 * Setters stay in the container's thin wrapper.
 *
 * SCRUM-898: authors are derived from `blogData.posts` (the full blog), never the
 * search-mutated local `Posts` state.
 */
export function deriveAuthors(
  blogData: { posts?: BlogPostLike[] } | null | undefined
): { authors: AuthorEntry[]; blogAvailability: boolean; postsAvailability: boolean } {
  if (!blogData) {
    return { authors: [], blogAvailability: false, postsAvailability: false };
  }
  if (!blogData.posts) {
    return { authors: [], blogAvailability: true, postsAvailability: false };
  }
  // Bound once: the narrowing from the guard above does not reach into the
  // `.filter` callback below, and the two reads must see the same list anyway.
  const posts = blogData.posts;
  const authorsLocal: AuthorEntry[] = posts.map((post) => ({
    _id: post.author?._id,
    name: post.author?.name,
    avatar: post.author?.avatar,
    pathauthor: post?.pathauthor,
    posts: posts
      .filter((p) => p.author?._id === post.author?._id)
      .map((p) => p.title),
  }));
  return {
    authors: getUnique(authorsLocal, "_id"),
    blogAvailability: true,
    postsAvailability: true,
  };
}
